import forEach from 'lodash/forEach'
import includes from 'lodash/includes'
import sortBy from 'lodash/sortBy'
import split from 'lodash/split'
import Head from 'next/head'
import NextImage, { ImageLoaderProps, ImageProps } from 'next/image'
import React, { FC, useMemo } from 'react'

import { imageURL } from '@Globals/Cloudinary'
import { Image as ImageData } from '@Globals/Image'

export interface ImageOptions {
  path: string
}

type ImagePropsLegacy = {
  options: ImageOptions
} & Omit<JSX.IntrinsicElements['img'], 'src'>

export const Image: FC<ImagePropsLegacy> = (
  {
    alt = '',
    options: { path },
    ...rest
  },
) => (
  <img
    alt={alt}
    src={path}
    {...rest}
  />
)

const cloudinaryLoader = ({
  quality,
  src,
  width,
}: ImageLoaderProps): string => imageURL(src, { quality, width })

interface ContentstackLoaderProps extends Omit<ImageLoaderProps, 'quality'> {
  cropRatio?: string
  objectFit?: ImageProps['objectFit']
  objectPosition?: ImageProps['objectPosition']
  quality?: number | string
}

const contentstackLoader = (
  {
    cropRatio,
    objectFit,
    objectPosition = 'center top',
    quality = '60,90',
    src,
    width,
  }: ContentstackLoaderProps,
) => {
  const url = new URL(src)

  url.searchParams.set('format', 'pjpg')
  url.searchParams.set('auto', 'webp')

  if (quality) {
    url.searchParams.set('quality', quality.toString())
  }

  if (width) {
    url.searchParams.set('width', width.toString())
  }

  if (objectFit === 'cover' && cropRatio) {
    const offset = {
      bottom: '100',
      center: '50',
      left: '0',
      right: '100',
      top: '0',
    }

    const [offsetX, offsetY] = split(objectPosition.toString(), ' ')

    const cropParam = `${cropRatio},offset-x${offset[offsetX]},offset-y${offset[offsetY]}`

    url.searchParams.set('crop', cropParam)
  }

  return url.toString()
}

export const generateProductLinkingDataImage = (src: string): string => contentstackLoader({
  src,
  width: 250,
})

interface ImageV2Props extends ImageProps {
  cropRatio?: string
}

export const ImageV2: FC<ImageV2Props> = (props) => {
  const { cropRatio, objectFit, objectPosition, src, ...rest } = props
  return (
    <NextImage
      loader={includes(src as string, 'cloudinary')
        ? cloudinaryLoader
        : params => contentstackLoader({ ...params, cropRatio, objectFit, objectPosition })}
      objectFit={objectFit}
      src={src}
      {...rest}
    />
  )
}

type IMGStyleProps = NonNullable<JSX.IntrinsicElements['img']['style']>

/** number is converted to pixels (px), and string is parsed as viewport width (vw) */
type PixelsOrViewportWidth = number | string

type Breakpoint = [
  /** min-width breakpoint */
  breakpoint: number,
  /** max container width before next breakpoint */
  width: PixelsOrViewportWidth,
  /** max container height */
  height?: PixelsOrViewportWidth,
]

export type PictureV2Props = JSX.IntrinsicElements['img'] & {
  breakpoints: Breakpoint[]
  /** Only applies to desktop */
  cropOnMobile?: boolean
  cropRatio?: string
  desktop: ImageData
  mobile?: ImageData
  objectFit?: IMGStyleProps['objectFit']
  objectPosition?: IMGStyleProps['objectPosition']
  priority?: boolean
}

interface SourceData {
  breakpoint: number
  srcSet: string
}

interface PriorityTag {
  media: string
  srcSet: string
}

const generateSrcSet = ({ width, ...props }: ContentstackLoaderProps) => [
  contentstackLoader({ width, ...props }),
  ', ',
  contentstackLoader({ width: width * 2, ...props }),
  ' 2x',
].join('')

const calculateBreakpointWidth = (breakpoint: number, width: PixelsOrViewportWidth): number => {
  if (typeof width === 'number') {
    return width
  }

  return (breakpoint / 100) * parseInt(width, 10)
}

export const PictureV2: FC<PictureV2Props> = ({
  alt = '',
  breakpoints,
  cropOnMobile,
  cropRatio,
  desktop,
  mobile,
  objectFit,
  objectPosition,
  priority = false,
  ...imgProps
}) => {
  const desktopURL = desktop.url
  const desktopDimensions = desktop.dimensions
  const mobileURL = mobile?.url || desktopURL
  const mobileDimensions = mobile?.dimensions || desktopDimensions

  const { priorities, sources, spanInner, spanOuter } = useMemo(() => {
    // sort by breakpoint ascending
    const breakpointsSorted = sortBy(breakpoints, 0)
    const priorityTags: PriorityTag[] = []
    const sourcesInner: SourceData[] = []
    const spanInnerStyle = {}
    const spanOuterStyle = {}

    forEach(
      breakpointsSorted,
      ([breakpoint], index) => {
        const next = breakpointsSorted[index + 1]
        const hasMoreBreakpoints = !!breakpointsSorted[index + 2]

        if (!next) {
          return
        }

        const [nextBreakpoint, nextWidth] = next
        const isDesktop = breakpoint >= 768
        const targetDimensions = isDesktop ? desktopDimensions : mobileDimensions
        const srcSet = generateSrcSet({
          cropRatio: isDesktop || cropOnMobile ? cropRatio : undefined,
          objectFit,
          objectPosition,
          src: isDesktop ? desktopURL : mobileURL,
          width: calculateBreakpointWidth(nextBreakpoint, nextWidth),
        })

        if (index === 0) {
          priorityTags.push({
            media: `(max-width: ${nextBreakpoint - 0.0001}px)`,
            srcSet,
          })
        } else if (!hasMoreBreakpoints) {
          priorityTags.push({
            media: `(min-width: ${breakpoint}px)`,
            srcSet,
          })
        } else {
          priorityTags.push({
            media: `(min-width: ${breakpoint}px) and (max-width: ${nextBreakpoint - 0.0001}px)`,
            srcSet,
          })
        }

        if (cropRatio && isDesktop && !cropOnMobile) {
          spanInnerStyle[`@media(min-width: ${breakpoint}px)`] = {
            display: 'none',
          }
          spanOuterStyle[`@media(min-width: ${breakpoint}px)`] = {
            inset: 0,
            position: 'absolute',
          }
        } else {
          spanInnerStyle[`@media(min-width: ${breakpoint}px)`] = {
            paddingTop: `${(targetDimensions.height / targetDimensions.width) * 100}%`,
          }
        }

        sourcesInner.push({ breakpoint, srcSet })
      },
    )

    return {
      priorities: priorityTags,
      // sort by breakpoint descending
      sources: sortBy(sourcesInner, ({ breakpoint: minWidth }) => -minWidth),
      spanInner: spanInnerStyle,
      spanOuter: spanOuterStyle,
    }
  }, [
    breakpoints,
    cropOnMobile,
    cropRatio,
    desktopDimensions,
    desktopURL,
    mobileDimensions,
    mobileURL,
    objectFit,
    objectPosition,
  ])

  return (
    <span
      css={{
        background: 'none',
        border: 0,
        boxSizing: 'border-box',
        display: 'block',
        height: 'initial',
        margin: 0,
        opacity: 1,
        overflow: 'hidden',
        padding: 0,
        position: 'relative',
        width: 'initial',

        ...spanOuter,
      }}
    >
      <span
        css={{
          background: 'none',
          border: '0px',
          boxSizing: 'border-box',
          display: 'block',
          height: 'initial',
          margin: '0px',
          opacity: 1,
          width: 'initial',

          padding: 0,
          paddingTop: `${(mobileDimensions.height / mobileDimensions.width) * 100}%`,
          ...spanInner,
        }}
      />
      <picture
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        {sources.map(({ breakpoint, srcSet }) => (
          <source
            key={breakpoint}
            media={`(min-width: ${breakpoint}px)`}
            srcSet={srcSet}
          />
        ))}
        <img
          alt={alt}
          css={{
            border: 'none',
            boxSizing: 'border-box',
            display: 'block',
            height: 0,
            inset: 0,
            margin: 'auto',
            maxHeight: '100%',
            maxWidth: '100%',
            minHeight: '100%',
            minWidth: '100%',
            objectFit,
            objectPosition,
            padding: 0,
            position: 'absolute',
            width: 0,
          }}
          srcSet={generateSrcSet({ src: mobileURL, width: 320 })}
          {...imgProps}
        />
      </picture>
      {priority && (
        <Head>
          {priorities.map(item => (
            <link
              key={`__picture-${item.srcSet}-${item.media}`}
              as="image"
              imageSrcSet={item.srcSet}
              media={item.media}
              rel="preload"
            />
          ))}
        </Head>
      )}
    </span>
  )
}
